import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import type { InputTaskCategory } from 'interfaces/IInputTask';
import { getInputYear } from 'utils/FarmUtils';
import { computeGrossMargin, resolveGmAreaHa } from './computeGrossMargin';
import {
  computeQuantityRequired,
  type QuantityRequiredLine,
} from './computeQuantityRequired';

/*
 * Wider-scope context for the Live totals panel (OP-2392), across the whole
 * organisation:
 *   - Quantity required caption: every crop in the same season — products are
 *     ordered for the season, not per crop.
 *   - Financial risk "plan GM against the crop average": the same crop and
 *     season.
 *
 * Kept in its own file (not computeLiveTotals.ts) so it doesn't collide with
 * other in-flight Live totals work. Pure functions — no hooks, no Redux.
 */

/** One crop in one season — the "same crop and season" the GM average uses. */
export interface CropSeason {
  /** Case-insensitive grouping key — matches groupInputsByCrop's crop key. */
  cropKey: string;
  /** Display name — the first spelling seen. */
  cropName: string;
  /** Season year, from the plan's start date. */
  year: number;
}

const cropKeyOf = (input: IInput): string | null => {
  const cropName = input.crop?.cropName?.trim();
  return cropName ? cropName.toLowerCase() : null;
};

/**
 * The single season shared by every scoped input, or null when the scope is
 * empty, spans seasons, or any plan is undated — the caption names exactly
 * one season.
 */
export const resolveSeason = (inputs: IInput[]): number | null => {
  let result: number | null = null;
  for (const input of inputs) {
    const year = getInputYear(input);
    if (year === null) return null;
    if (result === null) result = year;
    else if (result !== year) return null;
  }
  return result;
};

/**
 * The single crop and season shared by every scoped input, or null when the
 * scope is empty, mixes crops or seasons, or any plan is uncropped/undated.
 */
export const resolveCropSeason = (inputs: IInput[]): CropSeason | null => {
  let result: CropSeason | null = null;

  for (const input of inputs) {
    const cropKey = cropKeyOf(input);
    const year = getInputYear(input);
    if (cropKey === null || year === null) return null;

    if (result === null) {
      result = {
        cropKey,
        cropName: input.crop.cropName.trim(),
        year,
      };
    } else if (result.cropKey !== cropKey || result.year !== year) {
      return null;
    }
  }

  return result;
};

/** Every org input growing the given crop in the given season. */
export const filterCropSeasonInputs = (
  orgInputs: IInput[],
  cropSeason: CropSeason
): IInput[] =>
  orgInputs.filter(
    (input) =>
      cropKeyOf(input) === cropSeason.cropKey &&
      getInputYear(input) === cropSeason.year
  );

/**
 * Per-product quantity across every crop in the season, organisation-wide,
 * keyed the same way as computeQuantityRequired's lines (globalId, else item
 * name) so each scoped row can look up its wider-scope total directly.
 */
export const computeWiderScopeQuantities = (
  orgInputs: IInput[],
  year: number,
  gUnits: GUnit[],
  categories: Set<InputTaskCategory>
): Map<string, QuantityRequiredLine> => {
  const { lines } = computeQuantityRequired(
    orgInputs.filter((input) => getInputYear(input) === year),
    gUnits,
    categories
  );
  return new Map(lines.map((line) => [line.key, line]));
};

/** Expected (mid yield × mid price) GM and the area it's spread over, for one plan. */
const expectedGmWithArea = (
  input: IInput,
  gUnits: GUnit[],
  categories: Set<InputTaskCategory>
): { gm: number; areaHa: number } | null => {
  const result = computeGrossMargin(input, categories, gUnits);
  if (!result.hasData) return null;
  const areaHa = resolveGmAreaHa(result);
  if (areaHa <= 0) return null;
  return { gm: result.gm[1][1], areaHa };
};

export interface CropAverageGm {
  cropSeason: CropSeason;
  /** This plan's expected GM in $/ha, or null when it has no margin data. */
  planGmPerHa: number | null;
  /**
   * Area-weighted expected GM $/ha across the other plans of the same crop
   * and season in the organisation — Σ GM ÷ Σ area, so a large paddock weighs
   * more than a small one. Null when there are no other plans with margin data.
   */
  averageGmPerHa: number | null;
  /** Total area (ha) behind the average — shown so a thin average isn't over-read. */
  averageAreaHa: number;
}

/**
 * Compares one plan's expected GM $/ha with the average for the same crop and
 * season across the organisation. The plan itself is excluded from the
 * average so it is never compared with itself. Null when the plan has no crop
 * or season.
 */
export const computeCropAverageGm = (
  plan: IInput,
  orgInputs: IInput[],
  gUnits: GUnit[],
  categories: Set<InputTaskCategory>
): CropAverageGm | null => {
  const cropSeason = resolveCropSeason([plan]);
  if (!cropSeason) return null;

  const own = expectedGmWithArea(plan, gUnits, categories);

  let gmSum = 0;
  let areaSum = 0;

  for (const input of filterCropSeasonInputs(orgInputs, cropSeason)) {
    if (input.id === plan.id) continue;
    const other = expectedGmWithArea(input, gUnits, categories);
    if (!other) continue;
    gmSum += other.gm;
    areaSum += other.areaHa;
  }

  return {
    cropSeason,
    planGmPerHa: own ? own.gm / own.areaHa : null,
    averageGmPerHa: areaSum > 0 ? gmSum / areaSum : null,
    averageAreaHa: areaSum,
  };
};
